import type { EnemyInstance } from './enemies'
import {
  resolveStatusApplication,
  type StatusApplicationResult,
  type StatusHostTraits
} from '../combat/status/resolveStatusApplication'
import type { StatusApplicationResultKind } from '../combat/status/meta'

export type StatusEffectId = 'frost' | 'poison' | 'burn' | 'deepFreeze' | 'armorBreak'

/** Slow magnitudes — synergy is an explicit rule, not a sum of individuals. */
export const FROST_SLOW = 0.2
export const POISON_SLOW = 0.25
export const FROST_POISON_SYNERGY_SLOW = 0.7
export const MAX_SLOW = 0.7

export const FROST_DURATION = 2.5
export const POISON_DURATION = 4
export const POISON_DPS = 8

export const BURN_DURATION = 3
export const BURN_DPS = 12
export const DEEP_FREEZE_DURATION = 0.6

/** Mage Armor Break — duration / magnitude by level (tuned). */
export const ARMOR_BREAK_DURATION_LV = [1.2, 1.5, 1.8, 2.1] as const
/** Fraction of armorValue negated while broken (clamped, never negative armor). */
export const ARMOR_BREAK_MAGNITUDE_LV = [0.35, 0.42, 0.5, 0.55] as const
/** LV4 FRACTURE: stronger temporary magnitude on apply. */
export const ARMOR_BREAK_FRACTURE_MAGNITUDE = 0.7

export const SHOCKWAVE_INNER_RADIUS = 1
export const SHOCKWAVE_BONUS_DAMAGE = 60

export const CONTAGION_RADIUS = 1.5
export const CONTAGION_MAX_TARGETS = 2

/** Spec attached to a tower level / projectile payload. */
export interface TowerEffectSpec {
  id: StatusEffectId
  duration: number
  /** Individual slow when this effect is alone (ignored under synergy). */
  slow: number
  /** DoT tick rate in HP per simulated second. Frost/DeepFreeze/ArmorBreak use 0. */
  damagePerSecond: number
  /** LV4 Poison contagion flag carried onto the active effect. */
  contagious?: boolean
  /** Armor Break: fraction of armorValue negated (0–1). */
  armorBreakMagnitude?: number
}

/** Active effect on an enemy. At most one instance per id. */
export interface StatusEffectInstance {
  id: StatusEffectId
  remainingDuration: number
  slow: number
  damagePerSecond: number
  /** Tower that last applied/refreshed this effect (DoT ownership). */
  sourceTowerId?: string
  /** When true, death may spread Poison (LV4 Contagion). */
  contagious?: boolean
  /** Active armor-break magnitude (fraction). */
  armorBreakMagnitude?: number
}

export interface DotTick {
  amount: number
  effectId: StatusEffectId
  sourceTowerId?: string
}

export function hasStatus(enemy: EnemyInstance, id: StatusEffectId): boolean {
  return enemy.statusEffects.some((effect) => effect.id === id)
}

export function findStatus(
  enemy: EnemyInstance,
  id: StatusEffectId
): StatusEffectInstance | undefined {
  return enemy.statusEffects.find((effect) => effect.id === id)
}

/**
 * Effective slow from Frost/Poison only.
 * Deep Freeze is a separate immobilized state (see effectiveSpeed).
 * Frost+Poison → 70%. Capped at MAX_SLOW.
 */
export function effectiveSlow(enemy: EnemyInstance): number {
  const frost = findStatus(enemy, 'frost')
  const poison = findStatus(enemy, 'poison')
  let slow = 0
  if (frost && poison) slow = FROST_POISON_SYNERGY_SLOW
  else if (frost) slow = frost.slow
  else if (poison) slow = poison.slow
  return Math.min(slow, MAX_SLOW)
}

export function effectiveSpeed(baseSpeed: number, enemy: EnemyInstance): number {
  if (hasStatus(enemy, 'deepFreeze')) return 0
  return baseSpeed * (1 - effectiveSlow(enemy))
}

/**
 * Apply or refresh a status. Same id never stacks magnitude/DPS —
 * duration and params are replaced deterministically (REFRESH policy).
 * Latest application owns subsequent DoT for that effect id.
 * V2 immunities/duration resistance via optional traits (ACTIVE enemies: identity).
 */
export function applyStatus(
  enemy: EnemyInstance,
  spec: TowerEffectSpec,
  sourceTowerId?: string,
  traits?: StatusHostTraits
): StatusApplicationResultKind {
  if (!enemy.alive) return 'REJECTED'
  const host: StatusHostTraits = traits ?? {
    hasStatus: (id) => hasStatus(enemy, id)
  }
  if (!host.hasStatus) {
    host.hasStatus = (id) => hasStatus(enemy, id)
  }
  const resolved = resolveStatusApplication(
    {
      immuneStatuses: host.immuneStatuses,
      immuneStatusCategories: host.immuneStatusCategories,
      statusDurationMultiplier: host.statusDurationMultiplier,
      statusCategoryDurationMultipliers: host.statusCategoryDurationMultipliers,
      hasStatus: (id) => hasStatus(enemy, id)
    },
    spec
  )
  if (resolved.kind === 'IMMUNE' || resolved.kind === 'RESISTED' || resolved.kind === 'REJECTED') {
    return resolved.kind
  }
  const duration = resolved.appliedDuration
  const existing = findStatus(enemy, spec.id)
  if (existing) {
    existing.remainingDuration = duration
    existing.slow = spec.slow
    existing.damagePerSecond = spec.damagePerSecond
    if (sourceTowerId !== undefined) {
      existing.sourceTowerId = sourceTowerId
    }
    existing.contagious = spec.contagious === true
    if (spec.armorBreakMagnitude !== undefined) {
      existing.armorBreakMagnitude = spec.armorBreakMagnitude
    }
    return 'REFRESHED'
  }
  enemy.statusEffects.push({
    id: spec.id,
    remainingDuration: duration,
    slow: spec.slow,
    damagePerSecond: spec.damagePerSecond,
    sourceTowerId,
    contagious: spec.contagious === true,
    armorBreakMagnitude: spec.armorBreakMagnitude
  })
  return 'APPLIED'
}

/** Effective armor value after Armor Break (never negative). */
export function effectiveArmorValue(baseArmor: number, enemy: EnemyInstance): number {
  const br = findStatus(enemy, 'armorBreak')
  const mag = br?.armorBreakMagnitude ?? 0
  const clampedMag = Math.min(1, Math.max(0, Number.isFinite(mag) ? mag : 0))
  const reduced = baseArmor * (1 - clampedMag)
  return Math.max(0, reduced)
}

export function statusRemaining(enemy: EnemyInstance, id: StatusEffectId): number {
  return findStatus(enemy, id)?.remainingDuration ?? 0
}

/**
 * Advance status timers and accumulate DoT damage per effect for this slice.
 * Session applies each tick via attributed applyDamage.
 */
export function tickStatusEffects(enemy: EnemyInstance, dt: number): DotTick[] {
  if (!enemy.alive || dt <= 0) return []
  const ticks: DotTick[] = []
  const kept: StatusEffectInstance[] = []
  for (const effect of enemy.statusEffects) {
    if (effect.damagePerSecond > 0) {
      ticks.push({
        amount: effect.damagePerSecond * dt,
        effectId: effect.id,
        sourceTowerId: effect.sourceTowerId
      })
    }
    effect.remainingDuration -= dt
    if (effect.remainingDuration > 1e-9) {
      kept.push(effect)
    }
  }
  enemy.statusEffects = kept
  return ticks
}
